import { ClientSongCollection, getClientSongCollection, getClientSongCollections } from '@round/api';
import { useCallback, useState } from 'react';
import useAbortableEffect from '../../../../Hooks/useAbortableEffect';
import { SelectBoxOptionWithId } from '../../../../ui/DataEntry/SelectBox/SelectBox.types.';

const PAGE_SIZE = 25;
const mapCollectionToOption = (collection: ClientSongCollection): SelectBoxOptionWithId => ({
    id: collection.id,
    name: collection.name,
    picture: collection.picture ?? '',
});

export default function useSelectClientSongCollection(clientId: number, initialCollectionId?: number) {
    const [selectedCollection, setSelectedCollection] = useState<SelectBoxOptionWithId>();
    const [initialCollectionLoading, setInitialCollectionLoading] = useState(false);
    const [hasErrorLoadingInitialCollection, setHasErrorLoadingInitialCollection] = useState(false);

    useAbortableEffect(
        (signal) => {
            async function fetchInitialCollection(collectionId: number) {
                try {
                    setInitialCollectionLoading(true);
                    const response = await getClientSongCollection({ clientId, collectionId }, { signal });
                    if (response.status === 200) {
                        setSelectedCollection(mapCollectionToOption(response.data));
                        return;
                    }

                    setHasErrorLoadingInitialCollection(true);
                } catch (e) {
                    if (e instanceof Error && e.name === 'AbortError') {
                        return;
                    }

                    setHasErrorLoadingInitialCollection(true);
                } finally {
                    setInitialCollectionLoading(false);
                }
            }

            if (typeof initialCollectionId === 'number' && !selectedCollection) {
                fetchInitialCollection(initialCollectionId);
            }
        },
        [clientId, initialCollectionId, selectedCollection]
    );

    const [page, setPage] = useState<number>(1);
    const [search, setSearch] = useState('');

    const [collections, setCollections] = useState<ClientSongCollection[]>([]);
    const [count, setCount] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);

    const [isInitialized, setIsInitialized] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasErrorLoading, setHasErrorLoading] = useState(false);

    const fetchData = useCallback(async (...params: Parameters<typeof getClientSongCollections>) => {
        const response = await getClientSongCollections(...params);
        if (response.status === 200) {
            setCollections((collections) => collections.concat(response.data.results));
            setCount(response.data.count);
            setHasNextPage(Boolean(response.data.next));
            return true;
        }

        return false;
    }, []);

    useAbortableEffect(
        (signal) => {
            if (!isInitialized) {
                setIsLoading(true);
                fetchData({ clientId, page: 1, search, page_size: PAGE_SIZE }, { signal })
                    .then((success) => {
                        setIsInitialized(success);
                        setHasErrorLoading(!success);
                    })
                    .catch((e) => {
                        if (e.name === 'AbortError') {
                            return;
                        }

                        setHasErrorLoading(true);
                    })
                    .finally(() => setIsLoading(false));
            }
        },
        [clientId, fetchData, isInitialized, search]
    );

    const reset = () => {
        setIsInitialized(false);
        setPage(1);
        setIsLoading(false);
        setCollections([]);
        setHasErrorLoading(false);
        setHasNextPage(false);
        setCount(0);
    };

    const loadMore = async () => {
        if (!hasNextPage) {
            return;
        }

        try {
            setIsLoading(true);
            const success = await fetchData({ clientId, page: page + 1, search, page_size: PAGE_SIZE });
            if (success) {
                setPage((page) => page + 1);
                return;
            }

            setHasErrorLoading(true);
        } catch (e) {
            setHasErrorLoading(true);
        } finally {
            setIsLoading(false);
        }
    };

    return {
        selectedCollection,
        onSelectedCollectionChange: setSelectedCollection,
        initialCollectionLoading,
        hasErrorLoadingInitialCollection,
        collections: collections.map(mapCollectionToOption),
        search,
        count,
        isLoading,
        hasErrorLoading,
        loadMore,
        isInitialized,
        onSearchChange: (search: string) => {
            reset();
            setSearch(search);
        },
        addCollection: (collection: ClientSongCollection) => {
            setCollections((collections) => collections.concat(collection));
            setCount((count) => count + 1);
        },
        updateCollection: (collection: ClientSongCollection) =>
            setCollections((collections) => collections.map((c) => (c.id === collection.id ? collection : c))),
    };
}
